if (!window.DROPProductControls) {
  window.DROPProductControls = {
    scope(element) {
      return element?.closest('.thb-product-detail') || element?.closest('.shopify-section, [id^="shopify-section-"]') || null;
    },
    form(element) {
      if (element?.matches('form')) return element;
      const nested = element?.matches('product-form') && element.querySelector('form[data-type="add-to-cart-form"]');
      if (nested) return nested;
      const root = this.scope(element);
      const id = `product-form-${element?.dataset.section}`;
      return Array.from(root?.querySelectorAll('form') || []).find((form) => form.id === id) || null;
    },
    buttons(form) {
      return Array.from(this.scope(form)?.querySelectorAll('.single-add-to-cart-button') || []).filter((button) =>
        button.form === form || button.closest('form') === form || button.getAttribute('form') === form.id
      );
    },
    owners(form) {
      return Array.from(this.scope(form)?.querySelectorAll('product-form') || []).filter((owner) => this.form(owner) === form);
    },
    selectors(form) {
      return Array.from(this.scope(form)?.querySelectorAll('variant-selects, variant-radios') || []).filter((selector) => this.form(selector) === form);
    },
    element(form, id) {
      return Array.from(this.scope(form)?.querySelectorAll('[id]') || []).find((element) => element.id === id) || null;
    },
    strings() {
      return window.theme?.variantStrings || {};
    },
    showMessage(form, message = '', options = {}) {
      this.owners(form).forEach((owner) => {
        let region = owner.querySelector('.product-form__error-message-wrapper');
        if (!region && message) {
          region = document.createElement('div');
          region.className = 'product-form__error-message-wrapper form-notification error';
          const output = document.createElement('span');
          output.className = 'product-form__error-message';
          region.append(output);
          owner.prepend(region);
        }
        const output = region?.querySelector('.product-form__error-message');
        if (!output) return;
        output.textContent = message || '';
        if (message && options.cartLink) {
          const link = document.createElement('a');
          link.href = window.theme?.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
          link.textContent = this.strings().viewCart || 'View cart';
          output.append(document.createTextNode(' '), link);
        }
        region.setAttribute('role', options.success ? 'status' : 'alert');
        region.classList.toggle('error', !options.success);
        region.classList.toggle('success', !!options.success);
        region.toggleAttribute('hidden', !message);
      });
    },
    sync(form) {
      const state = form.dataset.dropVariantState;
      const ready = !state || state === 'ready';
      const busy = !!form.dropAddRequest;
      const input = form.querySelector('[name="id"]');
      if (input) {
        input.disabled = !ready;
        if (state && !ready && state !== 'sold-out') input.value = '';
      }
      form.setAttribute('aria-busy', String(state === 'pending' || busy));
      const strings = this.strings();
      const preorder = form.closest('product-form')?.getAttribute('template')?.includes('pre-order');
      this.buttons(form).forEach((button) => {
        button.disabled = !ready || busy;
        button.classList.toggle('loading', busy);
        button.classList.toggle('sold-out', state === 'sold-out' || state === 'unavailable');
        if (!ready || busy) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
        const label = button.querySelector('.single-add-to-cart-button--text');
        const text = state === 'sold-out' ? strings.soldOut : state === 'error' || state === 'unavailable' ? strings.unavailable : preorder ? strings.preOrder : strings.addToCart;
        if (label && text && state !== 'pending') label.textContent = text;
      });
      if (!form.dropVariantPaymentState) form.dropVariantPaymentState = new Map();
      form.querySelectorAll('.shopify-payment-button').forEach((payment) => {
        if (!ready || busy) {
          if (!form.dropVariantPaymentState.has(payment)) {
            form.dropVariantPaymentState.set(payment, { inert: payment.hasAttribute('inert'), ariaDisabled: payment.getAttribute('aria-disabled') });
          }
          payment.setAttribute('inert', '');
          payment.setAttribute('aria-disabled', 'true');
        }
      });
      if (ready && !busy) {
        form.dropVariantPaymentState.forEach((previous, payment) => {
          payment.toggleAttribute('inert', previous.inert);
          if (previous.ariaDisabled === null) payment.removeAttribute('aria-disabled');
          else payment.setAttribute('aria-disabled', previous.ariaDisabled);
        });
        form.dropVariantPaymentState.clear();
      }
    },
    refreshCart() {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    },
    sectionUrl(productUrl, params) {
      const url = new URL(productUrl, window.location.href);
      Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
      return url.pathname + url.search;
    }
  };
}

if (!customElements.get('variant-selects')) {

  /**
   *  @class
   *  @function VariantSelects
   */
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', this.onVariantChange);
    }

    connectedCallback() {
      this.sticky = this.dataset.sticky;
      this.isDisabledFeature = this.dataset.isDisabled;
      this.productWrapper = window.DROPProductControls.scope(this);
      this.productSlider = this.productWrapper?.querySelector('.product-images, .product-quick-images');
      this.thumbnails = this.productWrapper?.querySelector('.product-thumbnail-container');
      this.hideVariants = this.productSlider?.dataset.hideVariants === 'true';
      this.updateOptions();
      this.updateMasterId();
      this.setDisabled();
      this.setImageSet();
      this.prefetchProductUrls();
    }

    disconnectedCallback() {
      const request = this.variantRequest;
      if (request && !request.completed) {
        clearTimeout(request.timeout);
        request.controller.abort();
        if (request.form.dropVariantRequest === request) request.form.dropVariantRequest = null;
      }
    }

    get form() {
      return window.DROPProductControls.form(this);
    }

    get other() {
      return window.DROPProductControls.selectors(this.form).filter((selector) => selector !== this);
    }

    get updateUrl() {
      return [this, ...this.other].some((selector) => selector.dataset.updateUrl === 'true');
    }

    static showVariantRequestError(form, message) {
      const text = message === false ? '' : message || window.DROPProductControls.strings().variantRequestError || 'We could not confirm this selection. Please choose an option again or reload the page.';
      window.DROPProductControls.showMessage(form, text);
    }

    beginVariantRequest() {
      const form = this.form;
      if (!form) return null;
      const previous = form.dropVariantRequest;
      const request = { form, owner: this, controller: new AbortController(), failed: false, completed: false };
      form.dropVariantRequest = request;
      this.variantRequest = request;
      clearTimeout(previous?.timeout);
      previous?.controller.abort();
      [this, ...this.other].forEach((selector) => { selector.currentVariant = null; });
      this.setVariantPurchaseState(request, 'pending');
      VariantSelects.showVariantRequestError(form, false);
      request.timeout = setTimeout(() => this.failVariantRequest(request, new Error('Variant request timed out')), 12000);
      return request;
    }

    isCurrentVariantRequest(request) {
      return !!request && request.form.isConnected && this.isConnected && request.form.dropVariantRequest === request;
    }

    setVariantPurchaseState(request, state) {
      if (!this.isCurrentVariantRequest(request)) return;
      request.form.dataset.dropVariantState = state;
      window.DROPProductControls.sync(request.form);
    }

    failVariantRequest(request, error) {
      if (!this.isCurrentVariantRequest(request) || request.failed || request.completed) return;
      request.failed = true;
      clearTimeout(request.timeout);
      request.controller.abort();
      [this, ...this.other].forEach((selector) => { selector.currentVariant = null; });
      this.setVariantPurchaseState(request, 'error');
      this.hidePrices();
      VariantSelects.showVariantRequestError(request.form);
      if (error?.name !== 'AbortError') console.error(error);
    }

    hidePrices() {
      [`price-${this.dataset.section}`, `price-${this.dataset.section}--sticky`].forEach((id) => {
        window.DROPProductControls.element(this.form, id)?.classList.add('visibility-hidden');
      });
    }

    onVariantChange(event) {
      if (event?.target && !event.target.closest('fieldset')) return;
      this.updateOptions();
      this.updateVariantText();
      this.updateOther();
      return this.renderVariantInfo();
    }

    updateOptions() {
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = this.fieldsets.map((fieldset) => fieldset.querySelector('select')?.value || fieldset.querySelector('input:checked')?.value || '');
    }

    updateVariantText() {
      this.fieldsets.forEach((fieldset, index) => {
        const label = fieldset.querySelector('.form__label__value');
        if (label) label.textContent = this.options[index] || '';
      });
    }

    updateMasterId() {
      const span = window.DROPProductControls.element(this.form, `SelectedVariant-${this.dataset.section}`);
      const id = Number(span?.dataset.variantId);
      this.currentVariant = Number.isSafeInteger(id) && id > 0 ? {
        id,
        available: span.dataset.available === 'true',
        featured_media: span.dataset.featuredMediaId ? { id: Number(span.dataset.featuredMediaId) } : null
      } : null;
    }

    updateOther() {
      const ids = this.getSelectedValueIds();
      this.other.forEach((selector) => {
        const fieldsets = Array.from(selector.querySelectorAll('fieldset'));
        this.options.forEach((value, index) => {
          const fieldset = fieldsets[index];
          if (!fieldset) return;
          const select = fieldset.querySelector('select');
          if (select) select.value = value;
          else {
            fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
              radio.checked = ids[index] ? radio.dataset.valueId === ids[index] : radio.value === value;
            });
          }
        });
        selector.updateOptions();
        selector.updateVariantText();
      });
    }

    updateMedia() {
      if (!this.currentVariant) return;
      if (!this.currentVariant.featured_media) return;
      if (!this.productSlider) return;
      let mediaId = `#Slide-${this.dataset.section}-${this.currentVariant.featured_media.id}`;
      let activeMedia = this.productSlider.querySelector(mediaId);

      if (!activeMedia) return;
      if (this.thumbnails) {
        this.setActiveMediaSlider(mediaId, `#Thumb-${this.dataset.section}-${this.currentVariant.featured_media.id}`, this.productSlider);
      } else {
        this.setActiveMedia(activeMedia);
      }

    }
    setActiveMedia(activeMedia) {
      if (!activeMedia?.parentElement || !this.productSlider) return;

      this.productSlider.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });

      this.setImageSetMedia();

      activeMedia.classList.add('is-active');

      activeMedia.parentElement.prepend(activeMedia);

      if (!this.sticky) {
        // Scroll the gallery container to the start so the new active media is
        // visible. The window's scroll position is preserved by the surrounding
        // renderVariantInfo() flow — do not scroll the window here.
        window.setTimeout(() => {
          activeMedia.parentElement.scrollTo({
            left: 0,
            behavior: 'instant'
          });
        });
      }
    }
    setActiveMediaSlider(mediaId, thumbId, productSlider) {
      let flkty = typeof Flickity !== 'undefined' ? Flickity.data(productSlider) : null,
        activeMedia = productSlider.querySelector(mediaId);

      if (!activeMedia) return;
      if (flkty && this.hideVariants) {
        if (productSlider.querySelector('.product-images__slide.is-initial-selected')) {
          productSlider.querySelector('.product-images__slide.is-initial-selected').classList.remove('is-initial-selected');
        }
        [].forEach.call(productSlider.querySelectorAll('.product-images__slide-item--variant'), function (el) {
          el.classList.remove('is-active');
        });
        if (this.thumbnails) {
          if (this.thumbnails.querySelector('.product-thumbnail.is-initial-selected')) {
            this.thumbnails.querySelector('.product-thumbnail.is-initial-selected').classList.remove('is-initial-selected');
          }
          [].forEach.call(this.thumbnails.querySelectorAll('.product-images__slide-item--variant'), function (el) {
            el.classList.remove('is-active');
          });
        }

        activeMedia.classList.add('is-active');
        activeMedia.classList.add('is-initial-selected');

        this.setImageSetMedia();

        if (this.thumbnails) {
          let activeThumb = this.thumbnails.querySelector(thumbId);

          activeThumb?.classList.add('is-active');
          activeThumb?.classList.add('is-initial-selected');
        }

        productSlider.reInit(this.imageSetIndex);
        productSlider.selectCell(mediaId);

      } else if (flkty) {
        productSlider.selectCell(mediaId);
      }

    }

    updateURL() {
      if (!this.currentVariant || !this.updateUrl) return;
      const url = new URL(this.dataset.url, window.location.href);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({}, '', url.pathname + url.search);
    }

    updateVariantInput() {
      if (!this.currentVariant || !this.form) return;
      const root = window.DROPProductControls.scope(this.form);
      const forms = [this.form, ...Array.from(root.querySelectorAll('form')).filter((form) => form.id === 'product-form-installment')];
      forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updatePickupAvailability() {
      window.DROPProductControls.scope(this.form)?.querySelectorAll('.pickup-availability-wrapper').forEach((pickup) => {
        if (typeof pickup.update === 'function') pickup.update(this.currentVariant);
      });
    }

    removeErrorMessage() {
      window.DROPProductControls.showMessage(this.form);
    }

    getSectionsToRender() {
      return [`price-${this.dataset.section}`, `price-${this.dataset.section}--sticky`, `product-image-${this.dataset.section}--sticky`, `inventory-${this.dataset.section}`, `sku-${this.dataset.section}`, `quantity-${this.dataset.section}`];
    }

    getSelectedValueIds() {
      return this.fieldsets.map((fieldset) => {
        if (fieldset.querySelector('select')) {
          return fieldset.querySelector('select').selectedOptions[0]?.dataset.valueId;
        }
        return fieldset.querySelector('input:checked')?.dataset.valueId;
      }).filter(Boolean);
    }

    prefetchProductUrls() {
      if (!this.prefetchCache) this.prefetchCache = new Map();
      if (!this.prefetchTargets) this.prefetchTargets = new WeakSet();
      this.querySelectorAll('[data-product-url]').forEach((element) => {
        const productUrl = element.dataset.productUrl;
        if (!productUrl || productUrl === this.dataset.url) return;
        const target = element.tagName === 'OPTION' ? element.closest('select') : element.nextElementSibling || element.closest('fieldset');
        if (!target || this.prefetchTargets.has(target)) return;
        this.prefetchTargets.add(target);
        target.addEventListener('mouseenter', () => {
          if (this.prefetchCache.has(productUrl)) return;
          fetch(window.DROPProductControls.sectionUrl(productUrl, { section_id: this.dataset.section }))
            .then((response) => {
              if (!response.ok) throw new Error('Product prefetch failed');
              return response.text();
            })
            .then((text) => {
              if (this.isConnected) this.prefetchCache.set(productUrl, text);
            })
            .catch(() => {});
        }, { once: true });
      });
    }

    getProductUrl() {
      var currentUrl = this.dataset.url;
      for (var i = 0; i < this.fieldsets.length; i++) {
        var fieldset = this.fieldsets[i];
        var productUrl = null;
        var select = fieldset.querySelector('select');
        if (select) {
          var selected = select.selectedOptions[0];
          if (selected) {
            productUrl = selected.dataset.productUrl;
          }
        } else {
          var checked = fieldset.querySelector('input:checked');
          if (checked) {
            productUrl = checked.dataset.productUrl;
          }
        }
        if (productUrl && productUrl !== currentUrl) {
          return productUrl;
        }
      }
      return null;
    }

    switchProduct(productUrl, request) {
      const handleResponse = (text) => {
        if (!this.isCurrentVariantRequest(request) || request.failed) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const section = html.getElementById(`shopify-section-${this.dataset.section}`) || html;
        const nextRoot = section.querySelector('.thb-product-detail');
        const currentRoot = window.DROPProductControls.scope(this);
        const nextForm = nextRoot?.querySelector('form[data-type="add-to-cart-form"]');
        const nextId = Number(nextForm?.querySelector('[name="id"]')?.value);
        if (!nextRoot || !currentRoot?.matches('.thb-product-detail') || !Number.isSafeInteger(nextId) || nextId <= 0) {
          throw new Error('Missing product section in variant response');
        }
        const updateUrl = this.updateUrl;
        request.completed = true;
        clearTimeout(request.timeout);
        currentRoot.replaceWith(nextRoot);
        if (updateUrl) window.history.replaceState({}, '', productUrl);
        if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();
      };
      if (this.prefetchCache?.has(productUrl)) return Promise.resolve(this.prefetchCache.get(productUrl)).then(handleResponse);
      return fetch(window.DROPProductControls.sectionUrl(productUrl, { section_id: this.dataset.section }), { signal: request.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
          return response.text();
        })
        .then(handleResponse);
    }

    async renderVariantInfo() {
      const request = this.beginVariantRequest();
      if (!request) return;
      try {
        const ids = this.getSelectedValueIds();
        if (!ids.length || ids.length !== this.fieldsets.length) throw new Error('Missing selected option values');
        const productUrl = this.getProductUrl();
        if (productUrl) {
          await this.switchProduct(productUrl, request);
          return;
        }
        const response = await fetch(window.DROPProductControls.sectionUrl(this.dataset.url, { option_values: ids.join(','), section_id: this.dataset.section }), { signal: request.controller.signal });
        if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
        const responseText = await response.text();
        if (!this.isCurrentVariantRequest(request) || request.failed) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const span = html.getElementById(`SelectedVariant-${this.dataset.section}`);
        const sourceSelects = html.getElementById(`variant-selects-${this.dataset.section}`);
        if (!span || !sourceSelects) throw new Error('Missing selection state in variant response');
        if (sourceSelects.dataset.productId && this.dataset.productId && sourceSelects.dataset.productId !== this.dataset.productId) throw new Error('Unexpected product in variant response');
        [`price-${this.dataset.section}`, `price-${this.dataset.section}--sticky`].forEach((id) => {
          if (window.DROPProductControls.element(request.form, id) && !html.getElementById(id)) throw new Error('Missing price in variant response');
        });
        if (span.dataset.productUrl && span.dataset.productUrl !== this.dataset.url) {
          await this.switchProduct(span.dataset.productUrl, request);
          return;
        }
        const id = Number(span.dataset.variantId);
        const variant = Number.isSafeInteger(id) && id > 0 ? {
          id,
          available: span.dataset.available === 'true',
          featured_media: span.dataset.featuredMediaId ? { id: Number(span.dataset.featuredMediaId) } : null
        } : null;
        const selectors = [this, ...this.other];
        selectors.forEach((selector) => {
          selector.currentVariant = variant;
          sourceSelects.querySelectorAll('[data-value-id]').forEach((source) => {
            Array.from(selector.querySelectorAll('[data-value-id]')).filter((element) => element.dataset.valueId === source.dataset.valueId).forEach((element) => { element.dataset.available = source.dataset.available; });
          });
          const localSpan = selector.querySelector(`#SelectedVariant-${this.dataset.section}`);
          if (localSpan) Object.assign(localSpan.dataset, span.dataset);
          selector.setDisabled();
        });
        this.getSectionsToRender().forEach((id) => {
          if (!this.isCurrentVariantRequest(request) || request.failed) return;
          const destination = window.DROPProductControls.element(request.form, id);
          const source = html.getElementById(id);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            if (id.startsWith('price-')) destination.classList.remove('visibility-hidden');
          }
        });
        if (!this.isCurrentVariantRequest(request) || request.failed) return;
        if (!variant) {
          this.setVariantPurchaseState(request, 'unavailable');
          this.hidePrices();
        } else {
          this.updateVariantInput();
          if (!this.isCurrentVariantRequest(request) || request.failed) return;
          this.updateURL();
          this.setVariantPurchaseState(request, variant.available ? 'ready' : 'sold-out');
          this.updateMedia();
          this.updatePickupAvailability();
          dispatchCustomEvent('product:variant-change', { variant, variantTitle: this.options.join(' / '), sectionId: this.dataset.section, productRoot: window.DROPProductControls.scope(request.form) });
          publishStandardEvent('ProductSelectEvent', { product: this.dataset.productId ? { id: this.dataset.productId } : undefined, promise: Promise.resolve({ variant: { id: variant.id } }) }, this);
        }
        request.completed = true;
      } catch (error) {
        this.failVariantRequest(request, error);
      } finally {
        clearTimeout(request.timeout);
      }
    }

    toggleAddButton(disable = true, text = false) {
      if (!this.form) return;
      window.DROPProductControls.buttons(this.form).forEach((button) => {
        button.disabled = disable || !!this.form.dropAddRequest;
        const label = button.querySelector('.single-add-to-cart-button--text');
        if (text && label) label.textContent = text;
      });
    }

    setUnavailable() {
      if (!this.form) return;
      this.form.dataset.dropVariantState = 'unavailable';
      window.DROPProductControls.sync(this.form);
      this.hidePrices();
    }

    setDisabled() {
      if (this.isDisabledFeature != 'true') return;

      this.fieldsets.forEach((fieldset) => {
        if (fieldset.querySelector('select')) {
          fieldset.querySelectorAll('option').forEach((opt) => {
            opt.disabled = opt.dataset.available === 'false';
          });
        } else {
          fieldset.querySelectorAll('input').forEach((input) => {
            input.classList.toggle('is-disabled', input.dataset.available === 'false');
          });
        }
      });
      return true;
    }

    setImageSet() {
      if (!this.productSlider) return;

      let dataSetEl = this.productSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        this.imageSetIndex = Array.from(this.querySelectorAll('.product-form__input')).find((input) => input.dataset.handle === this.imageSetName)?.dataset.index;
        this.dataset.imageSetIndex = this.imageSetIndex;
        this.setImageSetMedia();
      }
    }

    setImageSetMedia() {
      if (!this.imageSetIndex) {
        return;
      }

      const optionPosition = parseInt(this.imageSetIndex.replace('option', ''), 10) - 1;
      const fieldset = this.fieldsets[optionPosition];
      if (!fieldset) return;
      const select = fieldset.querySelector('select');
      const checked = fieldset.querySelector('input:checked');
      let setValue;
      if (select && select.selectedOptions[0]) {
        setValue = select.selectedOptions[0].dataset.handle;
      } else if (checked) {
        setValue = checked.dataset.handle;
      }
      if (!setValue) return;
      let group = this.imageSetName + '_' + setValue;
      let selected_set_images = this.productWrapper.querySelectorAll(`.product-images__slide[data-set-name="${this.imageSetName}"]`),
        selected_set_thumbs = this.productWrapper.querySelectorAll(`.product-thumbnail[data-set-name="${this.imageSetName}"]`);
      if (this.hideVariants) {
        // Product images
        this.productWrapper.querySelectorAll('.product-images__slide').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_images.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });

        // Product thumbnails
        this.productWrapper.querySelectorAll('.product-thumbnail').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_thumbs.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });
      }

    }

  }
  customElements.define('variant-selects', VariantSelects);

  /**
   *  @class
   *  @function VariantRadios
   */
  class VariantRadios extends VariantSelects {
    constructor() {
      super();
    }

    updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.fieldsets = fieldsets;
      this.options = fieldsets.map((fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)?.value || '';
      });
    }
  }

  customElements.define('variant-radios', VariantRadios);
}

if (!customElements.get('product-slider')) {
  /**
   *  @class
   *  @function ProductSlider
   */
  class ProductSlider extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.pagination = this.parentElement.querySelector('.product-images-buttons');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');

      this.variantChangeHandler = this.variantChangeHandler || ((event) => {
        if (event.detail?.productRoot !== window.DROPProductControls.scope(this)) return;
        if (event.detail.variantTitle && event.detail.variantTitle !== 'Default Title') {
          this.dataset.variantTitle = event.detail.variantTitle;
          if (this.pswpElement) this.updatePswpProductInfo();
        }
      });
      document.removeEventListener('product:variant-change', this.variantChangeHandler);
      document.addEventListener('product:variant-change', this.variantChangeHandler);

      // Start Gallery
      this.galleryObserver?.disconnect();
      this.galleryObserver = new MutationObserver(() => {
        this.setupProductGallery();
      });

      this.galleryObserver.observe(this, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        characterData: false
      });

      this.setupProductGallery();

      // Start Pagination
      if (this.pagination) {
        this.setupPagination();
        this.resizeObserver = new ResizeObserver(entries => this.onPaginationResize());
        this.resizeObserver.observe(this);
        this.scrollHandler = this.scrollHandler || this.updatePagination.bind(this);
        this.removeEventListener('scroll', this.scrollHandler);
        this.addEventListener('scroll', this.scrollHandler);
      }
    }
    disconnectedCallback() {
      this.galleryObserver?.disconnect();
      this.resizeObserver?.disconnect();
      document.removeEventListener('product:variant-change', this.variantChangeHandler);
      this.removeEventListener('scroll', this.scrollHandler);
      this.prevButton?.removeEventListener('click', this.paginationClickHandler);
      this.nextButton?.removeEventListener('click', this.paginationClickHandler);
    }
    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }

      this.setEventListeners();

    }
    buildItems(activeImages) {
      let images = activeImages.map((item) => {
        let activelink = item.querySelector('.product-single__media-zoom');
        return {
          src: activelink.getAttribute('href'),
          msrc: activelink.dataset.msrc,
          thumbSrc: activelink.dataset.thumbSrc || activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h,
          title: activelink.getAttribute('title')
        };
      });
      return images;
    }
    buildPswpThumbnails(items) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      container.innerHTML = '';
      if (this.dataset.lightboxThumbnails === 'false' || items.length <= 1) {
        container.parentElement.style.display = 'none';
        return;
      }
      container.parentElement.style.display = '';
      items.forEach((item, index) => {
        const thumb = document.createElement('div');
        thumb.className = 'pswp__thumb';
        thumb.dataset.index = index;
        const img = document.createElement('img');
        img.src = item.thumbSrc;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
        container.appendChild(thumb);
      });
    }
    updatePswpProductInfo() {
      const infoEl = this.pswpElement.querySelector('.pswp__product-info');
      if (!infoEl) return;
      if (this.dataset.lightboxProductInfo === 'false') {
        infoEl.style.display = 'none';
        return;
      }
      infoEl.style.display = '';
      const titleEl = infoEl.querySelector('.pswp__product-title');
      const variantEl = infoEl.querySelector('.pswp__variant-title');
      if (titleEl) titleEl.textContent = this.dataset.productTitle || '';
      if (variantEl) variantEl.textContent = this.dataset.variantTitle || '';
    }
    syncPswpThumbnails(index) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      const thumbs = container.querySelectorAll('.pswp__thumb');
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
      });
      const activeThumb = container.querySelector('.pswp__thumb.is-active');
      if (activeThumb) {
        activeThumb.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
      }
    }
    setEventListeners() {
      this.activeImages = Array.from(this.querySelectorAll('.product-images__slide--image')).filter(element => element.clientWidth > 0);
      this.pswpElement = document.querySelectorAll('.pswp')[0];
      this.pswpOptions = {
        maxSpreadZoom: 2,
        loop: true,
        allowPanToNext: true,
        closeOnScroll: false,
        showHideOpacity: false,
        arrowKeys: true,
        history: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: true,
        arrowEl: true,
        preloaderEl: true
      };

      const _this = this;

      this.querySelectorAll('.product-single__media-zoom').forEach(function (link) {
        let thumbnail = link.closest('.product-single__media');
        let clone = link.cloneNode(true);
        thumbnail.append(clone);
        link.remove();
        clone.addEventListener('click', (e) => _this.zoomClick(e, clone));
      });

      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', function () {
          container.setAttribute('hidden', '');
        });
      });
    }
    zoomClick(e, link) {
      let items = this.buildItems(this.activeImages);
      let parent = link.closest('.product-images__slide');
      let i = this.activeImages.indexOf(parent);
      this.pswpOptions.index = parseInt(i, 10);
      this.pswpOptions.getThumbBoundsFn = () => {
        const thumbnail = link.closest('.product-single__media'),
          pageYScroll = window.scrollY || document.documentElement.scrollTop,
          rect = thumbnail.getBoundingClientRect();
        return {
          x: rect.left,
          y: rect.top + pageYScroll,
          w: rect.width
        };
      };
      if (typeof PhotoSwipe !== 'undefined') {
        this.buildPswpThumbnails(items);
        this.updatePswpProductInfo();

        const bottomBar = this.pswpElement.querySelector('.pswp__bottom-bar');
        if (bottomBar) {
          ['mousedown', 'touchstart', 'pointerdown'].forEach(evtName => {
            bottomBar.addEventListener(evtName, (evt) => evt.stopPropagation());
          });
        }

        let pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, items, this.pswpOptions);
        const _this = this;

        pswp.listen('firstUpdate', function () {
          pswp.listen('parseVerticalMargin', function (item) {
            item.vGap = {
              top: 50,
              bottom: 140
            };
          });
        });

        const scrollWrap = this.pswpElement.querySelector('.pswp__scroll-wrap');
        const resetScroll = function() {
          if (scrollWrap) scrollWrap.scrollLeft = 0;
          _this.pswpElement.scrollLeft = 0;
        };

        pswp.listen('beforeChange', function () {
          const index = pswp.getCurrentIndex();
          _this.syncPswpThumbnails(index);
          resetScroll();
        });

        const thumbsInner = this.pswpElement.querySelector('.pswp__thumbs-inner');
        if (thumbsInner) {
          thumbsInner.addEventListener('click', function handler(evt) {
            evt.stopPropagation();
            const thumb = evt.target.closest('.pswp__thumb');
            if (thumb) {
              pswp.goTo(parseInt(thumb.dataset.index, 10));
              resetScroll();
            }
          });
        }

        pswp.listen('close', function () {
          const container = _this.pswpElement.querySelector('.pswp__thumbs-inner');
          if (container) container.innerHTML = '';
        });

        pswp.init();
        this.syncPswpThumbnails(this.pswpOptions.index);
      }
      e.preventDefault();
    }
    setupPagination() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

      this.currentPageElement = this.pagination.querySelector('.slider-counter--current');
      this.pageTotalElement = this.pagination.querySelector('.slider-counter--total');

      this.prevButton = this.pagination.querySelector('button[name="previous"]');
      this.nextButton = this.pagination.querySelector('button[name="next"]');


      this.paginationClickHandler = this.paginationClickHandler || this.onPaginationButtonClick.bind(this);
      this.prevButton?.removeEventListener('click', this.paginationClickHandler);
      this.nextButton?.removeEventListener('click', this.paginationClickHandler);
      this.prevButton?.addEventListener('click', this.paginationClickHandler);
      this.nextButton?.addEventListener('click', this.paginationClickHandler);

      this.updatePagination();
    }
    onPaginationResize() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);

      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.updatePagination();
    }
    onPaginationButtonClick(event) {
      event.preventDefault();
      this.slideScrollPosition = event.currentTarget.name === 'next' ? this.scrollLeft + (1 * this.sliderItemOffset) : this.scrollLeft - (1 * this.sliderItemOffset);
      this.scrollTo({
        left: this.slideScrollPosition
      });
    }
    updatePagination() {
      if (!this.nextButton) return;

      const previousPage = this.currentPage;
      this.currentPage = Math.round(this.scrollLeft / this.sliderItemOffset) + 1;

      if (this.currentPageElement) {
        this.currentPageElement.textContent = this.currentPage;
      }
      if (this.pageTotalElement) {
        this.pageTotalElement.textContent = this.sliderItemsToShow.length;
      }

      if (this.currentPage != previousPage) {
        this.dispatchEvent(new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1]
          }
        }));
      }

      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.scrollLeft === 0) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }
    isSlideVisible(element, offset = 0) {
      const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
      return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft;
    }
  }
  customElements.define('product-slider', ProductSlider);
}

/**
 *  @class
 *  @function ProductForm
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = window.DROPProductControls.form(this);
      if (!this.form) return;
      const form = this.form;
      this.sticky = this.dataset.sticky;
      this.hideErrors = this.dataset.hideErrors === 'true';
      if (!form.dataset.dropVariantState) {
        const span = window.DROPProductControls.element(form, `SelectedVariant-${this.dataset.section}`);
        const soldOut = span ? span.dataset.available !== 'true' : window.DROPProductControls.buttons(form).some((button) => button.disabled);
        form.dataset.dropVariantState = soldOut ? 'sold-out' : 'ready';
      }
      window.DROPProductControls.sync(form);
      if (!this.sticky) {
        if (form.dropProductOwner && form.dropProductOwner !== this) form.dropProductOwner.disconnectedCallback();
        form.dropProductOwner = this;
        this.submitHandler = this.submitHandler || this.onSubmitHandler.bind(this);
        this.paymentGuard = this.paymentGuard || ((event) => {
          if (!event.target.closest?.('.shopify-payment-button')) return;
          if (form.dataset.dropVariantState === 'ready' && !form.dropAddRequest) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (!form.dropAddRequest) customElements.get('variant-selects')?.showVariantRequestError(form);
        });
        form.removeEventListener('submit', this.submitHandler);
        form.addEventListener('submit', this.submitHandler);
        form.removeEventListener('click', this.paymentGuard, true);
        form.addEventListener('click', this.paymentGuard, true);
      }
    }

    disconnectedCallback() {
      if (!this.form || this.sticky) return;
      this.form.removeEventListener('submit', this.submitHandler);
      this.form.removeEventListener('click', this.paymentGuard, true);
      if (this.form.dropProductOwner === this) this.form.dropProductOwner = null;
    }

    async onSubmitHandler(event) {
      event.preventDefault();
      const form = this.form;
      if (!form || form.dropAddRequest) return;
      if (form.dataset.dropVariantState !== 'ready') {
        customElements.get('variant-selects')?.showVariantRequestError(form);
        return;
      }
      const input = form.querySelector('[name="id"]');
      const id = Number(input?.value);
      if (!input || input.disabled || !Number.isSafeInteger(id) || id <= 0) {
        customElements.get('variant-selects')?.showVariantRequestError(form);
        return;
      }
      if (!form.reportValidity()) return;
      const request = { controller: new AbortController() };
      form.dropAddRequest = request;
      window.DROPProductControls.showMessage(form);
      window.DROPProductControls.sync(form);
      let formData;
      let timeout;
      let accepted = false;
      const strings = window.DROPProductControls.strings();
      try {
        formData = new FormData(form);
        formData.append('sections', this.getSectionsToRender().map((section) => section.section).join(','));
        formData.append('sections_url', window.location.pathname);
        const timedOut = new Promise((resolve, reject) => {
          timeout = setTimeout(() => {
            request.controller.abort();
            reject(new Error('Cart request timed out'));
          }, 20000);
        });
        const result = await Promise.race([
          (async () => {
            const response = await fetch(window.theme.routes.cart_add_url, {
              method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
              body: formData, signal: request.controller.signal
            });
            const data = await response.json();
            return { response, data };
          })(), timedOut
        ]);
        const { response, data } = result;
        if (!response.ok || data?.status) {
          let message = typeof data?.description === 'string' ? data.description : typeof data?.message === 'string' ? data.message : strings.cartRequestError || 'We could not add this item. Please check the quantity and try again.';
          if (data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
            const details = Object.values(data.errors).flat().filter((value) => typeof value === 'string');
            if (details.length) message = details.join(' ');
          }
          dispatchCustomEvent('product:variant-error', { source: 'product-form', productVariantId: formData.get('id'), errors: data?.errors || message, message: data?.message || message });
          message += ' ' + (strings.cartCheckAfterError || 'Check your cart before trying again.');
          window.DROPProductControls.showMessage(form, message, { cartLink: true });
          window.DROPProductControls.refreshCart();
          return;
        }
        const item = Array.isArray(data?.items) ? data.items[0] : data;
        if (!item || !Number.isSafeInteger(Number(item.variant_id || item.id)) || Number(item.variant_id || item.id) <= 0) throw new Error('Missing added item in cart response');
        accepted = true;
        let rendered = false;
        try {
          rendered = this.renderContents(data);
        } catch (error) {
          console.error(error);
        }
        dispatchCustomEvent('cart:item-added', { product: item });
        if (!rendered) {
          window.DROPProductControls.showMessage(form, strings.cartAddedRefreshError || 'Item added. Open your cart to confirm its latest contents before adding it again.', { cartLink: true, success: true });
          window.DROPProductControls.refreshCart();
        }
      } catch (error) {
        console.error(error);
        if (accepted) {
          window.DROPProductControls.showMessage(form, strings.cartAddedRefreshError || 'Item added. Open your cart to confirm its latest contents before adding it again.', { cartLink: true, success: true });
        } else {
          window.DROPProductControls.showMessage(form, strings.cartUnconfirmedError || 'We could not confirm the cart update. Check your cart before trying again.', { cartLink: true });
        }
        window.DROPProductControls.refreshCart();
      } finally {
        clearTimeout(timeout);
        if (form.dropAddRequest === request) form.dropAddRequest = null;
        if (form.isConnected) window.DROPProductControls.sync(form);
      }
    }

    getSectionsToRender() {
      return window.DROPCartUI.sections();
    }

    renderContents(parsedState) {
      if (!window.DROPCartUI.render(parsedState.sections)) return false;
      const productDrawer = document.getElementById('Product-Drawer');
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer && typeof OverlayManager !== 'undefined') {
        if (productDrawer?.contains(this)) OverlayManager.close(productDrawer);
        OverlayManager.open(cartDrawer);
        dispatchCustomEvent('cart-drawer:open');
      }
      return true;
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      if (typeof html !== 'string' || !html.trim()) return null;
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return section ? section.innerHTML : null;
    }

    handleErrorMessage(message = false) {
      window.DROPProductControls.showMessage(this.form, message || '');
    }
  });
}

/**
 *  @class
 *  @function ProductAddToCartSticky
 */
if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() {
      super();

      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
    }
    connectedCallback() {
      this.setupObservers();
      this.setupToggle();
    }
    setupToggle() {
      const button = this.querySelector('.product-add-to-cart-sticky--inner'),
        content = this.querySelector('.product-add-to-cart-sticky--content');

      if (this.animations_enabled) {
        const tl = gsap.timeline({
          reversed: true,
          paused: true,
          onStart: () => {
            button.classList.add('sticky-open');
          },
          onReverseComplete: () => {
            button.classList.remove('sticky-open');
          }
        });

        tl
          .set(content, {
            display: 'block',
            height: 'auto'
          }, 'start')
          .from(content, {
            height: 0,
            duration: 0.25
          }, 'start+=0.001');

        button.addEventListener('click', function () {
          tl.reversed() ? tl.play() : tl.reverse();

          return false;
        });
      } else {
        button.addEventListener('click', function () {
          content.classList.toggle('active');
          return false;
        });
      }


    }
    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }
    setupObservers() {
      let _this = this,
        observer = new IntersectionObserver(function (entries) {
          entries.forEach((entry) => {
            if (entry.target === footer) {
              if (entry.intersectionRatio > 0) {
                _this.classList.remove('sticky--visible');
              } else if (entry.intersectionRatio == 0 && _this.formPassed) {
                _this.classList.add('sticky--visible');
              }
            }
            if (entry.target === form) {
              let boundingRect = form.getBoundingClientRect();

              if (entry.intersectionRatio === 0 && window.scrollY > (boundingRect.top + boundingRect.height)) {
                _this.formPassed = true;
                _this.classList.add('sticky--visible');
              } else if (entry.intersectionRatio === 1) {
                _this.formPassed = false;
                _this.classList.remove('sticky--visible');
              }
            }
          });
        }, {
          threshold: [0, 1]
        }),
        form = window.DROPProductControls.form(this),
        footer = document.getElementById('footer');
      _this.formPassed = false;
      this._observer?.disconnect();
      this._observer = observer;
      if (form) observer.observe(form);
      if (footer) observer.observe(footer);
    }
  }

  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}

if (typeof addIdToRecentlyViewed !== "undefined") {
  addIdToRecentlyViewed();
}